import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Package } from 'lucide-react';
import { SignatureCanvas } from './SignatureCanvas';
import retiroCopiasService from '../../services/retiroCopiasService';
import type { OtroTramiteRecord } from '../../types';

interface RetiroMasivoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTramites: OtroTramiteRecord[];
  onSuccess: () => void;
}

interface FormErrors {
  nombreCompleto?: string;
  firmaDigital?: string;
}

export const RetiroMasivoDialog: React.FC<RetiroMasivoDialogProps> = ({
  open,
  onOpenChange,
  selectedTramites,
  onSuccess,
}) => {
  const queryClient = useQueryClient();

  const [fechaRetiro, setFechaRetiro] = useState(new Date().toISOString().split('T')[0]);
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [firmaDigital, setFirmaDigital] = useState<Blob | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setFechaRetiro(new Date().toISOString().split('T')[0]);
      setNombreCompleto('');
      setFirmaDigital(null);
      setErrors({});
      setGeneralError(null);
    }
  }, [open]);

  const retiroMasivoMutation = useMutation({
    mutationFn: () =>
      retiroCopiasService.registrarRetiroMasivo(
        selectedTramites.map((t) => t.id),
        fechaRetiro,
        { nombreCompleto, firmaDigital: firmaDigital ?? undefined }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Error al registrar el retiro masivo';
      setGeneralError(message);
    },
  });

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!nombreCompleto || nombreCompleto.trim().length < 3) {
      newErrors.nombreCompleto = 'El nombre completo debe tener al menos 3 caracteres';
    }

    if (!firmaDigital) {
      newErrors.firmaDigital = 'La firma digital es obligatoria';
    }

    setErrors(newErrors);
    const hasErrors = Object.keys(newErrors).length > 0;
    if (hasErrors) {
      setGeneralError('Por favor complete todos los campos obligatorios');
    } else {
      setGeneralError(null);
    }
    return !hasErrors;
  };

  const handleSubmit = async () => {
    if (validate()) {
      await retiroMasivoMutation.mutateAsync();
    }
  };

  const totalCopiasARetirar = selectedTramites.reduce(
    (sum, t) => sum + (t.copiasTotales - t.copiasActuales),
    0
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Retiro Masivo de Copias</DialogTitle>
          <DialogDescription>
            Se retirarán <strong>todas las copias disponibles</strong> de los {selectedTramites.length} trámites
            seleccionados ({totalCopiasARetirar} copias en total).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Listado de trámites seleccionados */}
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Trámites a retirar</span>
            </div>
            <div className="divide-y divide-gray-100 max-h-40 overflow-y-auto">
              {selectedTramites.map((tramite) => {
                const copias = tramite.copiasTotales - tramite.copiasActuales;
                return (
                  <div key={tramite.id} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
                    <span className="shrink-0 font-medium text-gray-800">{tramite.id}</span>
                    <span className="min-w-0 flex-1 truncate text-gray-500" title={tramite.tipo}>{tramite.tipo}</span>
                    <span className="shrink-0 text-gray-600">
                      {copias} {copias === 1 ? 'copia' : 'copias'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Fecha de retiro */}
          <div className="space-y-2">
            <Label htmlFor="fecha-retiro-masivo">Fecha de retiro</Label>
            <Input
              id="fecha-retiro-masivo"
              type="date"
              value={fechaRetiro}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setFechaRetiro(e.target.value)}
              disabled={retiroMasivoMutation.isPending}
            />
          </div>

          {/* Nombre del retirador */}
          <div className="space-y-2">
            <Label htmlFor="nombre-completo-masivo">
              Nombre Completo del Retirador
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="nombre-completo-masivo"
              type="text"
              value={nombreCompleto}
              onChange={(e) => setNombreCompleto(e.target.value)}
              placeholder="Nombre y apellido completo"
              disabled={retiroMasivoMutation.isPending}
              className={errors.nombreCompleto ? 'border-red-300' : ''}
            />
            {errors.nombreCompleto && (
              <p className="text-sm text-red-600">{errors.nombreCompleto}</p>
            )}
          </div>

          {/* Firma digital */}
          <SignatureCanvas
            onSave={setFirmaDigital}
            disabled={retiroMasivoMutation.isPending}
            error={errors.firmaDigital}
            label="Firma Digital"
            required
          />

          {firmaDigital && (
            <p className="text-sm text-green-600">✓ Firma capturada correctamente</p>
          )}
        </div>

        {generalError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{generalError}</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={retiroMasivoMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={retiroMasivoMutation.isPending}
            disabled={retiroMasivoMutation.isPending}
          >
            Registrar retiro masivo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
